import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

import { isBlocked } from '@/lib/blocklist';
import { getImageUrl } from '@/lib/polaroid-utils';
import { isValidAlbum } from '@/lib/album-utils';

/** Validate a feed event — kind 20 with valid image or kind 30006 album. */
function isValidFeedEvent(event: NostrEvent): boolean {
  if (event.kind === 30006) return isValidAlbum(event) && !isBlocked(event);
  if (event.kind !== 20) return false;
  if (!getImageUrl(event)) return false;
  if (isBlocked(event)) return false;
  return true;
}

const PAGE_SIZE = 15;

/** Query recent kind 20 picture events and kind 30006 albums with infinite scroll pagination. */
export function usePictures() {
  const { nostr } = useNostr();

  return useInfiniteQuery({
    queryKey: ['pictures', 'feed'],
    queryFn: async ({ pageParam }) => {
      const filter: NostrFilter = { kinds: [20, 30006], limit: PAGE_SIZE + 5 };
      if (pageParam) {
        filter.until = pageParam;
      }

      const events = await nostr.query(
        [filter],
        { signal: AbortSignal.timeout(5000) },
      );

      return events.filter(isValidFeedEvent).slice(0, PAGE_SIZE);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) return undefined;
      // Subtract 1 since 'until' is inclusive
      return lastPage[lastPage.length - 1].created_at - 1;
    },
    initialPageParam: undefined as number | undefined,
    staleTime: 60 * 1000,
  });
}
